import { useState, useEffect, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	AuthContext,
	type AuthContextType,
	type PositionStatusData,
} from '@/contexts/auth.context';
import { getPositionStatus } from '@/api/positions.api';
import { logError } from '@/utils/logger.utils';
import {
	PositionState,
	PositionInterviewingMode,
} from '@/types/server/enums.types';

// Auth Provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const navigate = useNavigate();
	const { positionId } = useParams<{ positionId: string }>();

	// Position status
	const [positionStatus, setPositionStatus] =
		useState<PositionStatusData | null>(null);

	// Admin authentication
	const [positionAdminAuthToken, setPositionAdminAuthToken] = useState<
		string | null
	>(localStorage.getItem('positionAdminAuthToken'));

	// Candidate authentication
	const [candidateAuthToken, setCandidateAuthToken] = useState<string | null>(
		null
	);

	// App state
	const [isInitializing, setIsInitializing] = useState(true);

	// Derived states
	const isAdminAuthenticated = positionStatus?.isAdminAuthenticated ?? false;
	const isCandidateAuthenticated = Boolean(candidateAuthToken);

	// Logout functions
	const logoutAdmin = () => {
		localStorage.removeItem('positionAdminAuthToken');
		setPositionAdminAuthToken(null);
	};

	const logoutCandidate = () => {
		localStorage.removeItem('candidateAuthToken');
		setCandidateAuthToken(null);
	};

	const clearAllAuth = () => {
		logoutAdmin();
		logoutCandidate();
		setPositionStatus(null);
	};

	// Helper to set admin token in localStorage
	const setPositionAdminAuthTokenWithStorage = (token: string | null) => {
		if (token) {
			localStorage.setItem('positionAdminAuthToken', token);
		} else {
			localStorage.removeItem('positionAdminAuthToken');
		}
		setPositionAdminAuthToken(token);
	};

	// Helper to set candidate token in localStorage
	const setCandidateAuthTokenWithStorage = (token: string | null) => {
		if (token) {
			localStorage.setItem('candidateAuthToken', token);
		} else {
			localStorage.removeItem('candidateAuthToken');
		}
		setCandidateAuthToken(token);
	};

	// Initialize auth state when the component mounts
	useEffect(() => {
		const initializeAuth = async () => {
			// Step 0: Clear candidate token on app mount
			localStorage.removeItem('candidateAuthToken');
			setCandidateAuthToken(null);

			if (!positionId) {
				// If no position ID in URL, redirect to generic 404
				navigate('/404');
				setIsInitializing(false);
				return;
			}

			try {
				// Step 1: Get position status
				const response = await getPositionStatus(
					positionId,
					positionAdminAuthToken || undefined
				);

				// Step 2-4: Handle different response codes
				// 404 -> Position not found (position-specific)
				// 500 -> Server error (position-specific)
				// Other non-200 -> Position not found
				if (response.httpResponseCode === 404) {
					navigate(`/${positionId}/not-found`);
					setIsInitializing(false);
					return;
				}

				if (response.httpResponseCode === 500) {
					navigate(`/${positionId}/500`);
					setIsInitializing(false);
					return;
				}

				if (response.httpResponseCode !== 200 || !response.data) {
					throw new Error('invalid status while fetching position status');
				}

				// Step 5: Validate response data
				const statusData: PositionStatusData = response.data;
				setPositionStatus(statusData);

				// Step 6: Handle inactive position
				if (statusData.state === PositionState.inactive) {
					navigate(`/${positionId}/inactive`);
					setIsInitializing(false);
					return;
				}

				// Step 7: Handle admin authentication
				if (!statusData.isAdminAuthenticated) {
					logoutAdmin();
				}

				// Step 8-9: Route based on authentication and interviewing mode
				if (
					statusData.interviewingMode === PositionInterviewingMode.offline &&
					!statusData.isAdminAuthenticated
				) {
					navigate(`/${positionId}/login/admin`);
				} else {
					navigate(`/${positionId}/login/candidate`);
				}
			} catch (err) {
				logError(err);
				navigate(`/${positionId}/500`);
			} finally {
				setIsInitializing(false);
			}
		};

		initializeAuth();
	}, [positionId, positionAdminAuthToken]);

	const authValue: AuthContextType = {
		positionStatus,
		setPositionStatus,
		isAdminAuthenticated,
		positionAdminAuthToken,
		setPositionAdminAuthToken: setPositionAdminAuthTokenWithStorage,
		isCandidateAuthenticated,
		candidateAuthToken,
		setCandidateAuthToken: setCandidateAuthTokenWithStorage,
		isInitializing,
		setIsInitializing,
		logoutAdmin,
		logoutCandidate,
		clearAllAuth,
	};

	return (
		<AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
	);
};
